// Consultas — peças comuns da tela do computador (routes/consultas.tsx) e
// da tela do app (mobile-consultations.tsx, pedido 2026-10-28): CPF,
// finalidade, montagem do perfil a partir do resultado e o PIX de crédito.
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  consultationCreditsService,
  type Consultation,
  type ConsultationCreditPix,
} from "@/services/gpsdrive-consultations.service";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { buildProfile, certidaoRecord, type Profile } from "@/components/consultas/profile";
import { legalProcesses, legalRecords, type LegalChecks } from "@/components/consultas/legal-checks";

export const PURPOSES = [
  "Cadastro / análise de cliente",
  "Contratação de funcionário ou motorista (com consentimento)",
  "Prevenção a fraude",
  "Cumprimento de obrigação legal ou contratual",
  "Outra",
];


export function errMsg(err: unknown): string {
  return err instanceof GpsDriveApiError || err instanceof Error ? err.message : "Tente novamente.";
}
// Resposta que não chegou (504 do nginx, rede caiu) não quer dizer que a
// consulta falhou — o backend termina, grava e cobra (2026-10-28: 504 aos
// 60 s com a consulta pronta aos 86 s). Manda conferir o histórico.
export function runErrMsg(err: unknown): string {
  const status = err instanceof GpsDriveApiError ? err.status : 0;
  if (status === 502 || status === 504 || !(err instanceof GpsDriveApiError))
    return "A consulta demorou mais que o normal e a resposta não chegou. Ela pode ter sido concluída: confira em \"Consultas feitas\" antes de consultar de novo (abrir não cobra).";
  return errMsg(err);
}
export function maskCpf(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}
export function validCpf(v: string): boolean {
  const d = v.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const calc = (n: number) => {
    let s = 0;
    for (let i = 0; i < n; i++) s += Number(d[i]) * (n + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(d[9]) && calc(10) === Number(d[10]);
}
export const fmtBrl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// O que cada tipo de consulta NEM pesquisa — painel diz "não incluído" em
// vez de "nenhum registro" (cliente achou que a consulta veio incompleta,
// 2026-10-10). CPF completo da fonte só traz identificação, endereços,
// telefones, e-mails e participação em empresas.
export const NOT_COVERED: Record<string, Profile["notCovered"]> = {
  completa: {},
  cpf_completo: { vehicles: true, processes: true, records: true },
  cpf_simples: {
    addresses: true,
    phones: true,
    emails: true,
    vehicles: true,
    processes: true,
    records: true,
    links: true,
  },
  antecedentes: {
    addresses: true,
    phones: true,
    emails: true,
    vehicles: true,
    processes: true,
    links: true,
  },
};


export function antecedentesOf(
  result: unknown,
): { status: "ok" | "error"; message?: string; data?: unknown } | null {
  const a =
    result && typeof result === "object"
      ? (result as { antecedentes?: unknown }).antecedentes
      : null;
  return a && typeof a === "object" && "status" in a
    ? (a as { status: "ok" | "error"; message?: string; data?: unknown })
    : null;
}

export function extractData(result: unknown): unknown {
  if (!result || typeof result !== "object") return result;
  const r = result as Record<string, unknown>;
  if ("dados" in r) return r.dados;
  const { status: _s, "API Full": _a, ...rest } = r;
  return rest;
}


// PIX de R$ 5,00 na conta Asaas da empresa. A tela confere o pagamento a
// cada 5 s (useConsultationCredits com polling) e, quando o saldo sobe,
// avisa e fecha sozinha.
export function PixCreditDialog({
  open,
  balance,
  price,
  needsBillingDocument,
  onClose,
}: {
  open: boolean;
  balance: number;
  price: number;
  // Admin/funcionário, 1ª compra: CPF/CNPJ da empresa pra cobrança da
  // plataforma antes de gerar o PIX.
  needsBillingDocument: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [pix, setPix] = useState<ConsultationCreditPix | null>(null);
  const [doc, setDoc] = useState("");
  // 1º passo: escolher o valor (pedido 2026-10-09: "15, 20, 25, 30...")
  // e, na 1ª compra de admin/funcionário, o CPF/CNPJ da empresa.
  const [choosing, setChoosing] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [amount, setAmount] = useState(price);
  const [custom, setCustom] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [approved, setApproved] = useState(false);
  const startBalance = useRef(balance);
  const presets = [1, 2, 3, 4, 5, 6, 10].map((n) => n * price);

  useEffect(() => {
    if (!open) return;
    startBalance.current = balance;
    setPix(null);
    setError(null);
    setApproved(false);
    setChoosing(true);
    setGenerating(false);
    setAmount(price);
    setCustom("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const generate = () => {
    const value = custom.trim() ? Number(custom.replace(",", ".")) : amount;
    if (!Number.isInteger(value) || value < price || value % price !== 0 || value > 1000) {
      setError(`Escolha um valor múltiplo de ${fmtBrl(price)} (até R$ 1.000,00).`);
      return;
    }
    let digits: string | undefined;
    if (needsBillingDocument) {
      digits = doc.replace(/\D/g, "");
      if (digits.length !== 11 && digits.length !== 14) {
        setError("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).");
        return;
      }
    }
    setError(null);
    setGenerating(true);
    consultationCreditsService
      .purchase(value, digits)
      .then((p) => {
        setPix(p);
        setChoosing(false);
        qc.invalidateQueries({ queryKey: ["consultations", "credits"] });
      })
      .catch((err) => setError(errMsg(err)))
      .finally(() => setGenerating(false));
  };

  useEffect(() => {
    if (!open || approved || balance <= startBalance.current) return;
    setApproved(true);
    const t = setTimeout(onClose, 1800);
    return () => clearTimeout(t);
  }, [open, approved, balance, onClose]);

  const copy = async () => {
    if (!pix) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // sem permissão de clipboard: o código continua visível pra copiar à mão
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar crédito para consultas</DialogTitle>
        </DialogHeader>
        {approved ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <div className="text-base font-semibold">Pagamento aprovado</div>
            <div className="text-sm text-muted-foreground">
              Saldo liberado: {fmtBrl(balance)}. Já pode consultar.
            </div>
          </div>
        ) : choosing ? (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Cada consulta custa <b>{fmtBrl(price)}</b>. Escolha quanto quer adicionar:
            </p>
            <div className="grid grid-cols-4 gap-2">
              {presets.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setAmount(v);
                    setCustom("");
                  }}
                  className={cn(
                    "rounded-lg border px-2 py-2 text-sm font-semibold transition-colors",
                    !custom.trim() && amount === v
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "hover:bg-muted",
                  )}
                >
                  {fmtBrl(v).replace(",00", "")}
                </button>
              ))}
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                Outro valor (R$, múltiplo de {fmtBrl(price)})
              </label>
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Ex.: 35"
                inputMode="numeric"
                className="h-10 w-full rounded-lg border bg-background px-3 text-sm"
              />
            </div>
            {needsBillingDocument && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  CPF ou CNPJ da empresa (só na primeira vez)
                </label>
                <input
                  value={doc}
                  onChange={(e) => setDoc(e.target.value)}
                  placeholder="00.000.000/0000-00"
                  inputMode="numeric"
                  className="h-10 w-full rounded-lg border bg-background px-3 text-sm"
                />
              </div>
            )}
            {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
            <Button className="w-full" onClick={generate} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Gerar PIX de {fmtBrl(custom.trim() ? Number(custom) || 0 : amount)} ·{" "}
              {Math.floor((custom.trim() ? Number(custom) || 0 : amount) / price)} consulta(s)
            </Button>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </div>
        ) : !pix ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Pague o PIX de <b>{fmtBrl(pix.value)}</b> no app do seu banco. Assim que o pagamento
              for aprovado, o crédito de {Math.floor(pix.value / price)} consulta(s) é liberado
              automaticamente.
            </p>
            <img
              src={`data:image/png;base64,${pix.encodedImage}`}
              alt="QR code PIX"
              className="mx-auto h-52 w-52 rounded-lg border bg-white p-2"
            />
            <div className="flex gap-2">
              <input
                readOnly
                value={pix.payload}
                className="h-9 min-w-0 flex-1 rounded-lg border bg-muted px-2.5 font-mono text-xs"
                onFocus={(e) => e.target.select()}
              />
              <Button size="sm" variant="outline" onClick={copy}>
                <Copy className="h-4 w-4" />
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aguardando confirmação do pagamento…
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {approved ? "Fechar" : "Fechar (pago depois)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Perfil estruturado de uma consulta (mesma regra nas duas telas). */
export function consultationProfile(c: Consultation, legal: LegalChecks | null): Profile {
  const data = extractData(c.result);
  const p = buildProfile(data, c.document);
  if (c.type === "antecedentes") p.records = [certidaoRecord(data, c.status === "sucesso", c.message)];
  // Completa (2026-10-12): certidão veio junto, em result.antecedentes.
  if (c.type === "completa") {
    const ant = antecedentesOf(c.result);
    p.records = ant
      ? [certidaoRecord(extractData(ant.data), ant.status === "ok", ant.message ?? null)]
      : [];
  }
  p.notCovered = NOT_COVERED[c.type];
  // Processos e restrições (2026-10-10) vêm em toda consulta nova.
  if (legal) {
    const seen = new Set(p.processes.map((x) => x.number));
    p.processes.push(...legalProcesses(legal).filter((x) => !seen.has(x.number)));
    p.records.push(...legalRecords(legal));
    if (p.notCovered) {
      const { processes: _p, records: _r, ...rest } = p.notCovered;
      p.notCovered = rest;
    }
  }
  return p;
}
